import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FileText,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  AlertCircle,
  CheckCircle,
  ClipboardCheck,
  BarChart3,
  ArrowRight,
  RefreshCw,
  Shield,
} from "lucide-react";

const ITAuditorDashboard = () => {
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [branchPerformanceData, setBranchPerformanceData] = useState([]);



  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];

  const getDashboardData = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(`/users/auditor/dashboard`, "GET");
      setDashboardData(response);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getSalesTrendData = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(`/report/sales-trend/`, "GET");
      setSalesTrendData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load sales trend data");
    } finally {
      setLoading(false);
    }
  };

  const getBranchPerformance = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(
        `/report/branch-performance/`,
        "GET"
      );
      setBranchPerformanceData(Array.isArray(response) ? response : []);
    } catch (error) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
    getSalesTrendData();
    getBranchPerformance();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
    trend,
    linkTo,
  }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 text-sm font-medium ${trend > 0 ? "text-green-600" : "text-red-600"
              }`}
          >
            <TrendingUp size={16} />
            {trend > 0 ? "+" : ""}
            {trend}%
          </div>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      {/* {linkTo && (
        <button
          onClick={() => (window.location.href = linkTo)}
          className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          View Details
          <ArrowRight size={14} />
        </button>
      )} */}
    </div>
  );

  const ReportCard = ({ title, description, icon: Icon, linkTo, badge }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all hover:border-blue-300">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg bg-blue-50`}>
          <Icon size={20} className="text-blue-600" />
        </div>
        {badge && (
          <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-xs text-gray-600 mb-3">{description}</p>
      <button
        onClick={() => (window.location.href = linkTo)}
        className="w-full bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
      >
        <FileText size={16} />
        Generate Report
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-gray-200 rounded-xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-40 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Shield size={36} className="text-blue-600" />
                IT Auditor Dashboard
              </h1>
              <p className="text-gray-600">
                Comprehensive analytics and reporting center
              </p>
            </div>
            <button
              onClick={getDashboardData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Current Month Sales"
            value={`GHS ${dashboardData?.monthly_sales}`}
            subtitle="Across all branches"
            icon={DollarSign}
            color="bg-green-500"
            linkTo="/auditor/sales/report"
          />
          <StatCard
            title="Active Branches"
            value={dashboardData?.active_branches}
            subtitle="All operational"
            icon={Package}
            color="bg-blue-500"
            linkTo="/auditor/retail/statement"
          />
          <StatCard
            title="Pending Returns"
            value={dashboardData?.pending_returns}
            subtitle="Awaiting authorization"
            icon={AlertCircle}
            color="bg-orange-500"
            linkTo="/auditor/authorize/returns"
          />
          <StatCard
            title="Supplier Accounts"
            value={dashboardData?.supplier_accounts}
            subtitle="Active suppliers"
            icon={Users}
            color="bg-purple-500"
            linkTo="/auditor/suppliers/statement"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Sales Trend Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 size={20} className="text-blue-600" />
                  Sales Trend Analysis
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Monthly performance vs last year
                </p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesTrendData || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Current Year"
                />
                <Line
                  type="monotone"
                  dataKey="last_year"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Last Year"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Branch Performance Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Branch Performance
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Sales distribution by branch this year
                </p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={branchPerformanceData || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {branchPerformanceData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Reports & Actions Section */}
        {/* <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Available Reports
              </h2>
              <p className="text-gray-600 mt-1">
                Generate comprehensive business reports
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ReportCard
              title="Sales Report"
              description="Detailed sales analysis and trends"
              icon={TrendingUp}
              linkTo="/auditor/sales/report"
            />
            <ReportCard
              title="Sales Details"
              description="Transaction-level sales breakdown"
              icon={FileText}
              linkTo="/auditor/sales/details"
            />
            <ReportCard
              title="Retail Statement"
              description="Branch-wise retail operations"
              icon={Package}
              linkTo="/auditor/retail/statement"
            />
            <ReportCard
              title="Warehouse Statement"
              description="Inventory and warehouse metrics"
              icon={Package}
              linkTo="/auditor/warehouse/statement"
            />
            <ReportCard
              title="Credit Report"
              description="Outstanding credit and payments"
              icon={DollarSign}
              linkTo="/auditor/credit/report"
              badge="New"
            />
            <ReportCard
              title="Suppliers Statement"
              description="Supplier accounts and transactions"
              icon={Users}
              linkTo="/auditor/suppliers/statement"
            />
            <ReportCard
              title="Authorize Returns"
              description="Review and approve return requests"
              icon={AlertCircle}
              linkTo="/auditor/authorize/returns"
              badge="12 Pending"
            />
            <ReportCard
              title="Restock & Reconciliation"
              description="Inventory restock and reconciliation"
              icon={ClipboardCheck}
              linkTo="/auditor/restock/reconciliation"
            />
          </div>
        </div> */}

        {/* Quick Actions */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle size={20} className="text-green-600" />
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              onClick={() => (window.location.href = "/auditor/sales/report")}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <TrendingUp size={16} />
              Generate Sales Report
            </button>
            <button
              onClick={() =>
                (window.location.href = "/auditor/authorize/returns")
              }
              className="bg-orange-50 hover:bg-orange-100 text-orange-700 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <AlertCircle size={16} />
              Review Returns
            </button>
            <button
              onClick={() =>
                (window.location.href = "/auditor/restock/reconciliation")
              }
              className="bg-green-50 hover:bg-green-100 text-green-700 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <ClipboardCheck size={16} />
              Start Reconciliation
            </button>
            <button
              onClick={() => (window.location.href = "/auditor/credit/report")}
              className="bg-purple-50 hover:bg-purple-100 text-purple-700 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <DollarSign size={16} />
              Credit Analysis
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ITAuditorDashboard;
